<template>
    <b-modal :id="modalId" :title="title" hide-footer size="lg" @show="show">
        <b-alert v-model="showInvalidFormAlert" variant="danger">
            <span v-if="prescriber && !prescriber.faxNumber">
                Prescriber doesn't have any fax number.
            </span>
        </b-alert>
        <b-row>
            <b-col v-if="formType == formTypeEnum.AlternateRxRequest" cols="6">
                <b>FROM: </b><span>{{ store.name }}</span><br>
                <span>{{ store.fullAddress }}</span><br>
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="6">
                <b>PATIENT: </b><span>{{ patient.displayNamesForPerson() }}</span><br>
                <span>{{ patient.fullAddress }}</span><br>
                <b>PATIENT DOB: </b><span>{{ patient.dateOfBirth | formatDate('L', false) }}</span>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="4">
                <b>PHONE: </b><span>{{ patient.phoneNumber }}</span><br>
            </b-col>
            <b-col cols="4">
                <b>CELL PHONE: </b>{{ patient.cellNumber }}<br>
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="9">
                <SearchComponent v-if="formType == formTypeEnum.AlternateRxRequest" v-model="selectedDrug"
                    :label="`Drug`" :search-u-r-l="`/Drug`"></SearchComponent>
            </b-col>
            <b-col cols="3">
                <b-button type="success" @click="addAlternateDrug">Add</b-button>
            </b-col>
        </b-row>

        <PrescriptionDetailsSummary v-if="prescription && formType == formTypeEnum.AlternateRxRequest"
            v-model="prescription" />
        <br>
        <b>Alternate Medications: </b>
        <b-table :fields="alternateMedicationFields" :items="alternateMedications"></b-table>

        <hr>

        <b-row>
            <b-col>
                <fg-input label="PHARMACY COMMENTS">
                    <b-form-textarea v-model="faxFormData.note" block-listener
                        placeholder="Write here any comments"></b-form-textarea>
                </fg-input>
            </b-col>
        </b-row>

        <b-row v-if="prescriber && prescriber.altFaxNumber">
            <b-col>
                <b-check v-model="useAltFax">
                    Use Alternate Fax Number ({{ prescriber.altFaxNumber }})
                </b-check>
            </b-col>
        </b-row>

        <hr>

        <b-row v-if="isSending">
            <b-spinner class="mr-2" label="Spinning"></b-spinner>
            <strong>Sending...</strong>
        </b-row>
        <b-row v-else>
            <b-col>
                <b-button block class="mt-3" @click="close">Close</b-button>
            </b-col>
            <b-col>
                <b-button :disabled="!isFormValid" block class="mt-3" variant="success" @click="send()">Send</b-button>
            </b-col>
        </b-row>

    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop } from "vue-property-decorator";
import { Store } from '@/models/Store';
import { Prescriber } from '@/models/Prescriber';
import { Patient } from '@/models/Patient';
import { FaxForm, Prescription } from '@/models/Prescription';
import { PatientDocument } from "@/models/PatientDocument";
import { FaxType } from "@/models/PrescriberFollowUpCase";
import { Drug } from "@/models/Drug/Drug";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PrescriptionDetailsSummary from '@/components/Prescription/PrescriptionDetailsSummary.vue'
import SearchComponent from '@/components/SearchComponent.vue';

@Component({
    name: "AlternateRxFormModal",
    components: { PrescriptionDetailsSummary, SearchComponent }
})
export default class AlternateRxFormModal extends Vue {
    @Prop({ default: () => new Store() }) private store!: Store;
    @Prop({ default: () => new Prescriber() }) private prescriber!: Prescriber;
    @Prop() private prescription!: Prescription;
    @Prop() private patient!: Patient;
    @Prop({ default: () => new Drug() }) private drug!: Drug;
    @Prop({ default: "" }) private modalId!: string;
    @Prop({ default: FaxType.RefillAuthorization }) private formType!: FaxType;

    private faxFormData: FaxForm = new FaxForm();
    private isSending: boolean = false;

    private selectedDrug: Drug = new Drug();
    private alternateMedications: Drug[] = [];

    private formTypeEnum = FaxType;

    protected useAltFax = false;

    get title(): string {
        if (this.formType == FaxType.AlternateRxRequest) return 'Alternate RX Request';

        return '';
    }

    get alternateMedicationFields() {
        return [
            { key: "ndc", label: "NDC" },
            { key: "productNameShort", label: "Drug Name" },
        ];
    }

    get endpointURI(): string {
        let res = '';
        if (this.formType == FaxType.AlternateRxRequest) res = 'alternate-rx-request';

        return res;
    }

    get prescriberToShow(): Prescriber {
        return this.prescriber;
    }

    get drugToShow(): Drug {
        return this.drug?.packageID ? this.drug : this.selectedDrug;
    }

    get isFormValid(): boolean {
        let validSelectedFields = this.formType == FaxType.AlternateRxRequest && !!this.selectedDrug.packageID
            || this.formType == FaxType.AlternateRxRequest;

        return !!this.prescriberToShow.faxNumber && validSelectedFields;
    }

    get showInvalidFormAlert(): boolean {
        return !this.isFormValid && this.formType != FaxType.AlternateRxRequest;
    }

    addAlternateDrug() {
        if (this.alternateMedications.length < 3) {
            this.alternateMedications.push(this.selectedDrug);
        }
    }

    show() {
    }

    send() {
        let errorMsg = `Error while sending ${this.title}.`;

        this.faxFormData.fax = this.useAltFax && this.prescriberToShow.altFaxNumber ?
            this.prescriberToShow.altFaxNumber : this.prescriberToShow.faxNumber;

        if (this.formType == FaxType.AlternateRxRequest) {
            this.faxFormData.prescriberID = Number(this.prescriber.id);
            this.faxFormData.patientID = Number(this.patient.id);
            this.faxFormData.packageID = Number(this.prescription.packageID);
            this.faxFormData.drugSource = Number(this.prescription.drugSource);
            this.faxFormData.alternateDrug = this.alternateMedications.map(d => `${d.ndc} - ${d.productNameShort}`).join("\n");
            this.faxFormData.storeID = Number(this.store.id);
        } else {
            this.faxFormData.rxID = this.prescription.rxID
        }

            this.isSending = true;
            this.$http.post<PatientDocument>(`prescription/${this.endpointURI}`, this.faxFormData)
                .then(res => {
                    if (res.data.id) {
                        let msg = this.title + " sent successfully and saved as a new patient document. A new Follow-Up case was created.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success)
                        );
                        this.$emit('saved');
                    }
                })
                .catch(err => {
                    this.$notification(NotificationOptions.error(err));
                    console.error(errorMsg, { err, response: err?.response })
                })
                .finally(() => this.isSending = false)
        }

    close() {
        this.$bvModal.hide(this.modalId);
        this.selectedDrug = new Drug();
    }

    clearForm() {
        this.faxFormData = new FaxForm();
    }
}
</script>

<style scoped></style>
